import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppHeader } from 'AppHeaderReact';
import { useErrorLogService } from 'ErrorLogging';
import { useSiteMetadata } from 'SearchEngineOptimization/useSiteMetadata';
import { streamDashboardPath, type StreamForStreamDashboard } from 'Lessons';
import { getEntityMetadataImageSrc, getStreamLogInfo } from '../selectors';
import '../locales/streamDashboard/streamDashboard-en.json';

// Seeds viewedStream for the header bookmark and clears it on unmount. Otherwise the header keeps the old course.
export function useStreamDashboardHeader(stream: StreamForStreamDashboard) {
    const { t } = useTranslation('back_royal', { keyPrefix: 'streamDashboard.streamDashboard' });
    const { setTitleHTML, setBodyBackground, setShowAlternateHomeButton } = useAppHeader();
    const SiteMetadata = useSiteMetadata();
    const ErrorLogService = useErrorLogService();

    useEffect(() => {
        setTitleHTML(t('course_details'));
        setBodyBackground('slate-grey-100-alt');
        setShowAlternateHomeButton(true);
    }, [t, setTitleHTML, setBodyBackground, setShowAlternateHomeButton]);

    useViewedStream(stream);

    // content_item_mixin.rb always builds entity_metadata, so report it when absent.
    useEffect(() => {
        if (!stream.entityMetadata) {
            ErrorLogService.notify('Received empty entity_metadata for stream', null, getStreamLogInfo(stream));
            return;
        }

        SiteMetadata.updateHeaderMetadata({
            title: stream.entityMetadata.title ?? undefined,
            description: stream.entityMetadata.description ?? undefined,
            path: stream.entityMetadata.canonicalUrl ?? undefined,
            image: getEntityMetadataImageSrc(stream) ?? undefined,
        });
    }, [stream, SiteMetadata, ErrorLogService]);
}

/*
    Seed the store once. The bookmark lives in the store after that. Depend on the fields, not on stream:
    stream changes on each progress refresh, and a reseed undoes a toggle.
*/
function useViewedStream(stream: StreamForStreamDashboard) {
    const { setViewedStream } = useAppHeader();
    const streamId = stream.id;
    const streamTitle = stream.title;
    const streamLocalePackId = stream.localePackId;
    const streamFavorite = stream.favorite;
    const shareCanonicalUrl = stream.entityMetadata?.canonicalUrl;
    const shareTweetTemplate = stream.entityMetadata?.tweetTemplate;
    const shareTitle = stream.entityMetadata?.title;
    const shareDescription = stream.entityMetadata?.description;
    const hasEntityMetadata = !!stream.entityMetadata;

    useEffect(() => {
        if (!streamId || !streamTitle || !streamLocalePackId) return undefined;

        setViewedStream({
            id: streamId,
            title: streamTitle,
            favorite: streamFavorite,
            streamDashboardPath: streamDashboardPath({ streamTitle, streamId }),
            locale_pack: { id: streamLocalePackId },
            // The header's share button reads this, and does nothing without it.
            entity_metadata: hasEntityMetadata
                ? {
                      canonical_url: shareCanonicalUrl ?? null,
                      tweet_template: shareTweetTemplate ?? null,
                      title: shareTitle ?? null,
                      description: shareDescription ?? null,
                  }
                : null,
        });

        return () => setViewedStream(null);
    }, [
        streamId,
        streamTitle,
        streamLocalePackId,
        streamFavorite,
        hasEntityMetadata,
        shareCanonicalUrl,
        shareTweetTemplate,
        shareTitle,
        shareDescription,
        setViewedStream,
    ]);
}
